import { c as _c } from "react/compiler-runtime";
import { type DashboardItem } from '@/components/Dashboard/types';
import { metricStatusText } from '@/components/DetailedView/utils';
import { Iframe } from '@/components/Metrics/MetricCard/Iframe';
import { NotShowable } from '@/components/Metrics/MetricCard/NotShowable';
import { Skeleton } from '@/components/Skeleton';
import { METRICS_CARD_MIN_HEIGHT, METRICS_PATH, STATISTICS_PATH } from '@/constants';
import { PowerBi } from '@/features/powerbi/PowerBi';
import type { Metric, Statistic } from '@/payload/payload.types';
import { StatusArrow, type StatusArrowProps } from '@tallinn-arvudes/ui/atoms/status-arrow';
import { Chart } from '@tallinn-arvudes/ui/chart';
import type { ChartContextOptions, ParsedChartReturn } from '@tallinn-arvudes/ui/chart/types';
import { KEY_EVENT_KEYS } from '@tallinn-arvudes/ui/constants';
import { cn } from '@tallinn-arvudes/ui/utils';
import Link from 'next/link';
import React, { type ReactNode } from 'react';
export type MetricsData = Pick<Metric, 'id' | 'positiveDirection' | 'targetLevel' | 'targetLevelDate' | 'targetPeriods' | 'realPeriods' | 'unit' | 'targetLevelDescription' | 'trendArrowBackground' | 'trendArrowDirection' | 'titlePlainText' | 'focusArea'>;
export type StatisticsData = Statistic;
export const MetricStatus = t0 => {
  const $ = _c(6);
  const {
    direction,
    background,
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? "medium" : t1;
  const t2 = direction === "no_data" ? "same" : direction;
  const t3 = direction === "no_data" ? "question" : "arrow";
  let t4;
  if ($[0] !== background || $[1] !== className || $[2] !== size || $[3] !== t2 || $[4] !== t3) {
    t4 = <StatusArrow pointerDirection={t2} size={size} type={t3} variant={background} className={className} />;
    $[0] = background;
    $[1] = className;
    $[2] = size;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
const MetricHeader = t0 => {
  const $ = _c(17);
  const {
    data,
    isEditing
  } = t0;
  let t1;
  if ($[0] !== data.trendArrowBackground || $[1] !== data.trendArrowDirection) {
    t1 = <div className="peer"><MetricStatus direction={data.trendArrowDirection} background={data.trendArrowBackground} /></div>;
    $[0] = data.trendArrowBackground;
    $[1] = data.trendArrowDirection;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = isEditing ? -1 : 0;
  const t3 = `${METRICS_PATH}/${data.id}`;
  const t4 = data.titlePlainText || "";
  let t5;
  if ($[3] !== data.titlePlainText || $[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = <Link tabIndex={t2} onClick={_temp} href={t3} title={t4} className="line-clamp-2 font-bold group-hover:text-semantic-content-brand block peer-hover:hidden max-h-fit">{data.titlePlainText}</Link>;
    $[3] = data.titlePlainText;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = data.titlePlainText || "";
  let t7;
  if ($[8] !== data) {
    t7 = metricStatusText(data);
    $[8] = data;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <p title={t6} className="font-bold group-hover:text-semantic-content-brand hidden peer-hover:block">{t7}</p>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t1 || $[14] !== t5 || $[15] !== t8) {
    t9 = <div className="flex gap-4">{t1}{t5}{t8}</div>;
    $[13] = t1;
    $[14] = t5;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
};
export const MetricCard = t0 => {
  const $ = _c(25);
  let cardRootProps;
  let onClick;
  let options;
  let parsedMetric;
  let rawMetric;
  if ($[0] !== t0) {
    ({
      rawMetric,
      parsedMetric,
      options,
      onClick,
      ...cardRootProps
    } = t0);
    $[0] = t0;
    $[1] = cardRootProps;
    $[2] = onClick;
    $[3] = options;
    $[4] = parsedMetric;
    $[5] = rawMetric;
  } else {
    cardRootProps = $[1];
    onClick = $[2];
    options = $[3];
    parsedMetric = $[4];
    rawMetric = $[5];
  }
  const {
    isLoading,
    isError,
    children,
    isEditing
  } = cardRootProps;
  if (isLoading || isError) {
    let t1;
    if ($[6] !== cardRootProps) {
      t1 = <MetricCardRoot {...cardRootProps} />;
      $[6] = cardRootProps;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  if (!rawMetric || !parsedMetric || !rawMetric.realPeriods || !rawMetric.targetPeriods) {
    let t1;
    if ($[8] !== cardRootProps) {
      t1 = <MetricCardRoot isInValid={true} {...cardRootProps} />;
      $[8] = cardRootProps;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    return t1;
  }
  let t1;
  if ($[10] !== isEditing || $[11] !== rawMetric) {
    t1 = <MetricHeader isEditing={isEditing} data={rawMetric} />;
    $[10] = isEditing;
    $[11] = rawMetric;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const t2 = rawMetric.focusArea ?? undefined;
  let t3;
  if ($[13] !== options || $[14] !== t2) {
    t3 = {
      highLightedZone: t2,
      ...options
    };
    $[13] = options;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== parsedMetric || $[17] !== t3) {
    t4 = <div className="pr-6 grow"><Chart options={t3} data={parsedMetric} /></div>;
    $[16] = parsedMetric;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== cardRootProps || $[20] !== children || $[21] !== onClick || $[22] !== t1 || $[23] !== t4) {
    t5 = <MetricCardRoot onClick={onClick} extraContent={children} {...cardRootProps}>{t1}{t4}</MetricCardRoot>;
    $[19] = cardRootProps;
    $[20] = children;
    $[21] = onClick;
    $[22] = t1;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
};
export const StatisticCard = t0 => {
  const $ = _c(28);
  let cardRootProps;
  let data;
  let onClick;
  if ($[0] !== t0) {
    ({
      data,
      onClick,
      ...cardRootProps
    } = t0);
    $[0] = t0;
    $[1] = cardRootProps;
    $[2] = data;
    $[3] = onClick;
  } else {
    cardRootProps = $[1];
    data = $[2];
    onClick = $[3];
  }
  let component = null;
  const {
    isLoading,
    isError,
    children,
    isEditing
  } = cardRootProps;
  if (isLoading || isError) {
    let t1;
    if ($[4] !== cardRootProps) {
      t1 = <MetricCardRoot {...cardRootProps} />;
      $[4] = cardRootProps;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  if (!data) {
    let t1;
    if ($[6] !== cardRootProps) {
      t1 = <MetricCardRoot isInValid={true} {...cardRootProps} />;
      $[6] = cardRootProps;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  if (data.type === "external") {
    let t1;
    if ($[8] !== data) {
      t1 = <NotShowable data={data} />;
      $[8] = data;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    component = t1;
  } else {
    if (data.type === "powerbi") {
      let t1;
      if ($[10] !== data || $[11] !== isEditing) {
        t1 = <PowerBi isEditing={isEditing} data={data} />;
        $[10] = data;
        $[11] = isEditing;
        $[12] = t1;
      } else {
        t1 = $[12];
      }
      component = t1;
    } else {
      if (data.type === "iframe" && data.url) {
        const t1 = isEditing ? -1 : 0;
        const t2 = data.titlePlainText || "Nimeta statistika";
        let t3;
        if ($[13] !== data.url || $[14] !== t1 || $[15] !== t2) {
          t3 = <Iframe tabIndex={t1} title={t2} className="h-full w-full" src={data.url} />;
          $[13] = data.url;
          $[14] = t1;
          $[15] = t2;
          $[16] = t3;
        } else {
          t3 = $[16];
        }
        component = t3;
      }
    }
  }
  if (!component) {
    return null;
  }
  const t1 = isEditing ? -1 : 0;
  const t2 = `${STATISTICS_PATH}/${data.id}`;
  const t3 = data.titlePlainText || "";
  let t4;
  if ($[17] !== data.titlePlainText || $[18] !== t1 || $[19] !== t2 || $[20] !== t3) {
    t4 = <div className="mb-2 flex justify-between"><Link tabIndex={t1} onClick={_temp2} href={t2} title={t3} className="line-clamp-2 font-bold group-hover:text-semantic-content-brand block peer-hover:hidden max-h-fit">{data.titlePlainText}</Link></div>;
    $[17] = data.titlePlainText;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== cardRootProps || $[23] !== children || $[24] !== component || $[25] !== onClick || $[26] !== t4) {
    t5 = <MetricCardRoot onClick={onClick} extraContent={children} {...cardRootProps}>{t4}{component}</MetricCardRoot>;
    $[22] = cardRootProps;
    $[23] = children;
    $[24] = component;
    $[25] = onClick;
    $[26] = t4;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  return t5;
};
type MetricCardCommonProps = {
  children?: ReactNode;
  contentClassName?: string;
  className?: string;
  isError?: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  item?: DashboardItem;
  isEditing?: boolean;
};
type MetricCardRootProps = MetricCardCommonProps & {
  extraContent?: ReactNode;
  isInValid?: boolean;
};
export const MetricCardRoot = t0 => {
  const $ = _c(45);
  const {
    contentClassName,
    className,
    isLoading,
    errorMessage,
    isError,
    children,
    extraContent,
    isInValid,
    onClick,
    item,
    isEditing
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        height: METRICS_CARD_MIN_HEIGHT
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== className) {
      t2 = cn("text-center h-full", className);
      $[1] = className;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== contentClassName) {
      t3 = cn("h-full flex items-center justify-center relative", contentClassName);
      $[3] = contentClassName;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Skeleton isLoading={true} className="h-full w-full absolute top-0 left-0 bg-semantic-surface-faint" />;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t2 || $[7] !== t3) {
      t5 = <MetricCardLayout style={t1} className={t2} contentClassName={t3}>{t4}</MetricCardLayout>;
      $[6] = t2;
      $[7] = t3;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  if (isError) {
    let t1;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        height: METRICS_CARD_MIN_HEIGHT
      };
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    let t2;
    if ($[10] !== className) {
      t2 = cn("h-full", className);
      $[10] = className;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    let t3;
    if ($[12] !== item?.name) {
      t3 = item?.name ? <p className="line-clamp-2 font-bold group-hover:text-semantic-content-brand block max-h-fit">{item?.name}</p> : null;
      $[12] = item?.name;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    let t4;
    if ($[14] !== contentClassName) {
      t4 = cn("h-full flex items-center justify-center text-sm text-semantic-content-subtle text-center px-1", contentClassName);
      $[14] = contentClassName;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] !== errorMessage || $[17] !== t4) {
      t5 = <div className={t4}>{errorMessage}</div>;
      $[16] = errorMessage;
      $[17] = t4;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== children || $[20] !== contentClassName || $[21] !== t2 || $[22] !== t3 || $[23] !== t5) {
      t6 = <MetricCardLayout style={t1} className={t2} contentClassName={contentClassName}>{t3}{t5}{children}</MetricCardLayout>;
      $[19] = children;
      $[20] = contentClassName;
      $[21] = t2;
      $[22] = t3;
      $[23] = t5;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    return t6;
  }
  if (isInValid) {
    let t1;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        height: METRICS_CARD_MIN_HEIGHT
      };
      $[25] = t1;
    } else {
      t1 = $[25];
    }
    let t2;
    if ($[26] !== className) {
      t2 = cn("text-center h-full", className);
      $[26] = className;
      $[27] = t2;
    } else {
      t2 = $[27];
    }
    let t3;
    if ($[28] !== contentClassName) {
      t3 = cn("h-full flex items-center justify-center", contentClassName);
      $[28] = contentClassName;
      $[29] = t3;
    } else {
      t3 = $[29];
    }
    let t4;
    if ($[30] !== errorMessage) {
      t4 = <div>Viga: {errorMessage}</div>;
      $[30] = errorMessage;
      $[31] = t4;
    } else {
      t4 = $[31];
    }
    let t5;
    if ($[32] !== children || $[33] !== t2 || $[34] !== t3 || $[35] !== t4) {
      t5 = <MetricCardLayout style={t1} className={t2} contentClassName={t3}>{t4}{children}</MetricCardLayout>;
      $[32] = children;
      $[33] = t2;
      $[34] = t3;
      $[35] = t4;
      $[36] = t5;
    } else {
      t5 = $[36];
    }
    return t5;
  }
  let t1;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      height: METRICS_CARD_MIN_HEIGHT
    };
    $[37] = t1;
  } else {
    t1 = $[37];
  }
  let t2;
  if ($[38] !== children || $[39] !== className || $[40] !== contentClassName || $[41] !== extraContent || $[42] !== isEditing || $[43] !== onClick) {
    t2 = <MetricCardLayout style={t1} className={className} contentClassName={contentClassName} onClick={onClick} isEditing={isEditing}>{children}{extraContent}</MetricCardLayout>;
    $[38] = children;
    $[39] = className;
    $[40] = contentClassName;
    $[41] = extraContent;
    $[42] = isEditing;
    $[43] = onClick;
    $[44] = t2;
  } else {
    t2 = $[44];
  }
  return t2;
};
export type MetricCardLayoutExtra = {
  onClick?: () => void;
};
export const MetricCardLayout = t0 => {
  const $ = _c(22);
  let children;
  let className;
  let contentClassName;
  let isEditing;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      contentClassName,
      children,
      isEditing,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = contentClassName;
    $[4] = isEditing;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    contentClassName = $[3];
    isEditing = $[4];
    props = $[5];
  }
  let t1;
  if ($[6] !== isEditing || $[7] !== props) {
    t1 = !isEditing && props.onClick ? {
      tabIndex: 0,
      role: "button",
      onKeyDown: e => {
        if (e.key === KEY_EVENT_KEYS.ENTER || e.key === KEY_EVENT_KEYS.SPACE) {
          props.onClick?.();
        }
      }
    } : {
      tabIndex: -1
    };
    $[6] = isEditing;
    $[7] = props;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const containerProps = t1;
  let t2;
  if ($[9] !== className) {
    t2 = cn("border-2 border-dotted border-transparent p-1 group", className);
    $[9] = className;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== contentClassName || $[12] !== props.onClick) {
    t3 = cn("bg-white p-4 h-full flex flex-col shadow-[2px_2px_6px_0_#00000026] group-hover:shadow-[0_0_0_2px_#0072CE] gap-2", {
      "cursor-pointer": props.onClick
    }, contentClassName);
    $[11] = contentClassName;
    $[12] = props.onClick;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== children || $[15] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[14] = children;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== containerProps || $[18] !== props || $[19] !== t2 || $[20] !== t4) {
    t5 = <div {...props} {...containerProps} suppressHydrationWarning={true} className={t2}>{t4}</div>;
    $[17] = containerProps;
    $[18] = props;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};
function _temp(e) {
  if (e.metaKey || e.ctrlKey || e.shiftKey) {
    e.stopPropagation();
    return;
  }
  e.preventDefault();
}
function _temp2(e) {
  if (e.metaKey || e.ctrlKey || e.shiftKey) {
    e.stopPropagation();
    return;
  }
  e.preventDefault();
}